import React, { useState, useEffect } from 'react';
import { 
  Cloud, CloudOff, RefreshCw, Settings, Lock, ArrowUpCircle,
  CheckCircle, XCircle, Clock, AlertTriangle, Loader2,
  Trash2, Play, Pause, ExternalLink, FileSpreadsheet
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

/**
 * AutoSyncPanel - SharePoint/Teams auto-sync configuration panel
 * Displays in Upload Center with tier-based gating
 */
const AutoSyncPanel = ({ projectId = null, className = '' }) => {
  const { user, tenant, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [syncing, setSyncing] = useState({});

  const tenantId = tenant?.id || user?.tenant?.id || user?.tenant_id;
  const userId = user?.id || user?._id;

  useEffect(() => {
    if (tenantId) {
      loadCapabilities();
      loadConfigs();
    }
  }, [tenantId, projectId]);

  const loadCapabilities = async () => {
    try {
      const res = await api.getSyncCapabilities(tenantId);
      setCapabilities(res.data);
    } catch (error) {
      console.error('Failed to load sync capabilities:', error);
    }
  };

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const res = await api.listSyncConfigs(tenantId, projectId);
      setConfigs(res.data.configs || []);
    } catch (error) {
      console.error('Failed to load sync configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncNow = async (configId) => {
    try {
      setSyncing(prev => ({ ...prev, [configId]: true }));
      await api.triggerSyncNow(configId, tenantId, userId);
      await loadConfigs();
    } catch (error) {
      console.error('Sync failed:', error);
      alert(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(prev => ({ ...prev, [configId]: false }));
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this sync configuration?')) {
      return;
    }
    
    try {
      await api.deleteSyncConfig(configId, tenantId);
      await loadConfigs();
    } catch (error) {
      console.error('Delete failed:', error);
      alert(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleEnabled = async (config) => {
    try {
      await api.updateSyncConfig(config.id, { enabled: !config.enabled }, tenantId, userId);
      await loadConfigs();
    } catch (error) {
      console.error('Toggle failed:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'syncing':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'pending':
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatFrequency = (freq) => {
    switch (freq) {
      case '2h': return 'Every 2 hours';
      case '4h': return 'Every 4 hours';
      case '8h': return 'Every 8 hours';
      case '24h': return 'Daily';
      default: return freq;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Tier-locked state (Starter/Growth)
  if (capabilities && !capabilities.capabilities.enabled) {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg ${className}`} data-testid="autosync-panel-locked">
        <div className="p-6 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CloudOff className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Auto-Sync via SharePoint / Teams
          </h3>
          <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
            Automatically sync your project data from SharePoint or Teams files. 
            Keep your portfolio data up-to-date without manual uploads.
          </p>
          <div className="flex items-center justify-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg mb-4">
            <Lock className="w-4 h-4" />
            <span className="text-sm font-medium">
              {capabilities.current_tier === 'starter' ? 'Starter' : 'Growth'} tier - Auto-sync not available
            </span>
          </div>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg font-medium hover:opacity-90">
            <ArrowUpCircle className="w-4 h-4" />
            Upgrade to Business
          </button>
          <p className="text-xs text-gray-400 mt-3">
            Business tier: Daily sync • Enterprise tier: Custom frequency + multi-file support
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-lg ${className}`} data-testid="autosync-panel">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Cloud className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Auto-Sync via SharePoint / Teams</h3>
            <p className="text-xs text-gray-500">
              {capabilities?.tier === 'enterprise' 
                ? 'Enterprise: Custom frequency, multi-file support'
                : 'Business: Daily sync, one file per project'}
            </p>
          </div>
        </div>
        
        {capabilities?.can_create && (
          <button
            onClick={() => setShowConfigModal(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm font-medium hover:opacity-90"
            data-testid="add-sync-config-btn"
          >
            <Settings className="w-4 h-4" />
            Configure Sync
          </button>
        )}
      </div>

      {/* Configs List */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : configs.length === 0 ? (
          <div className="text-center py-8">
            <FileSpreadsheet className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-4">No sync configurations yet</p>
            {capabilities?.can_create && (
              <button
                onClick={() => setShowConfigModal(true)}
                className="text-[var(--clr-brand-primary)] text-sm font-medium hover:underline"
              >
                Set up your first auto-sync
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {configs.map((config) => (
              <div
                key={config.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                data-testid={`sync-config-${config.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusIcon(config.sync_status)}
                      <span className="font-medium text-gray-900 truncate">
                        {config.file_path?.split('/').pop() || 'Unknown file'}
                      </span>
                      {!config.enabled && (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">
                          Paused
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate mb-2">
                      {config.sharepoint_site_url}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>Frequency: {formatFrequency(config.frequency)}</span>
                      <span>Last sync: {formatDate(config.last_sync)}</span>
                      {config.project_name && (
                        <span>Project: {config.project_name}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSyncNow(config.id)}
                      disabled={syncing[config.id]}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                      title="Sync Now"
                    >
                      {syncing[config.id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleToggleEnabled(config)}
                      className={`p-2 rounded-lg ${config.enabled ? 'text-gray-400 hover:bg-gray-100' : 'text-green-600 hover:bg-green-50'}`}
                      title={config.enabled ? 'Pause' : 'Resume'}
                    >
                      {config.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => {
                        setEditingConfig(config);
                        setShowConfigModal(true);
                      }}
                      className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg"
                      title="Edit"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteConfig(config.id)}
                      className="p-2 text-red-400 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
        <span>
          {configs.length} / {capabilities?.capabilities.max_configs === Infinity ? 'Unlimited' : capabilities?.capabilities.max_configs} configurations
        </span>
        <a
          href="https://docs.microsoft.com/graph"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-blue-600 hover:underline"
        >
          <ExternalLink className="w-3 h-3" />
          Microsoft Graph API Docs
        </a>
      </div>

      {/* Config Modal */}
      {showConfigModal && (
        <SyncConfigModal
          config={editingConfig}
          capabilities={capabilities}
          projectId={projectId}
          tenantId={tenantId}
          userId={userId}
          onClose={() => {
            setShowConfigModal(false);
            setEditingConfig(null);
          }}
          onSave={() => {
            setShowConfigModal(false);
            setEditingConfig(null);
            loadConfigs();
          }}
        />
      )}
    </div>
  );
};


/**
 * SyncConfigModal - Modal for creating/editing sync configurations
 */
const SyncConfigModal = ({ 
  config, 
  capabilities, 
  projectId, 
  tenantId, 
  userId, 
  onClose, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    project_id: config?.project_id || projectId || '',
    sharepoint_site_url: config?.sharepoint_site_url || '',
    file_path: config?.file_path || '',
    frequency: config?.frequency || '24h',
    workstream_mapping: config?.workstream_mapping || '',
    enabled: config?.enabled !== false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await api.getProjects({});
      setProjects(res.data.projects || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.project_id || !formData.sharepoint_site_url || !formData.file_path) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      
      if (config) {
        await api.updateSyncConfig(config.id, formData, tenantId, userId);
      } else {
        await api.createSyncConfig(formData, tenantId, userId);
      }
      
      onSave();
    } catch (err) {
      console.error('Save failed:', err);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object') {
        setError(detail.message || 'Failed to save configuration');
      } else {
        setError(detail || 'Failed to save configuration');
      }
    } finally {
      setSaving(false);
    }
  };

  const isEnterprise = capabilities?.tier === 'enterprise';
  const allowedFrequencies = capabilities?.capabilities?.frequencies || ['24h'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6" data-testid="sync-config-modal">
        <h3 className="text-lg font-semibold mb-4">
          {config ? 'Edit Sync Configuration' : 'Configure Auto-Sync'}
        </h3>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Project Selection */}
          {!projectId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.project_id}
                onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                required
              >
                <option value="">Select a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* SharePoint Site URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SharePoint Site URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={formData.sharepoint_site_url}
              onChange={(e) => setFormData({ ...formData, sharepoint_site_url: e.target.value })}
              placeholder="https://contoso.sharepoint.com/sites/PMO"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              required
            />
            <p className="text-xs text-gray-400 mt-1">
              The SharePoint site containing your Excel file
            </p>
          </div>

          {/* File Path */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              File Path <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.file_path}
              onChange={(e) => setFormData({ ...formData, file_path: e.target.value })}
              placeholder="/Shared Documents/Projects/ProjectData.xlsx"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              required
            />
            <p className="text-xs text-gray-400 mt-1">
              Path to the Excel file within the site
            </p>
          </div>

          {/* Sync Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sync Frequency
            </label>
            <select
              value={formData.frequency}
              onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              disabled={!isEnterprise && allowedFrequencies.length === 1}
            >
              {allowedFrequencies.map((freq) => (
                <option key={freq} value={freq}>
                  {freq === '24h' ? 'Daily (24 hours)' :
                   freq === '8h' ? 'Every 8 hours' :
                   freq === '4h' ? 'Every 4 hours' :
                   freq === '2h' ? 'Every 2 hours' : freq}
                </option>
              ))}
            </select>
            {!isEnterprise && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <Lock className="w-3 h-3" />
                Custom frequencies require Enterprise tier
              </p>
            )}
          </div>

          {/* Workstream Mapping (Enterprise only) */}
          {isEnterprise && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Workstream Mapping (Optional)
              </label>
              <input
                type="text"
                value={formData.workstream_mapping}
                onChange={(e) => setFormData({ ...formData, workstream_mapping: e.target.value })}
                placeholder="e.g., Development, QA, Design"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
              />
              <p className="text-xs text-gray-400 mt-1">
                Map this file to a specific workstream (multi-file support)
              </p>
            </div>
          )}

          {/* Enabled Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sync-enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="rounded border-gray-300 text-[var(--clr-brand-primary)] focus:ring-[var(--clr-brand-primary)]"
            />
            <label htmlFor="sync-enabled" className="text-sm text-gray-700">
              Enable automatic sync
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (config ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutoSyncPanel;
